/**
 * SearchableSelect.jsx
 *
 * Drop-in replacement for a native <select> that opens a popover with a
 * search input + filtered options list. Used for long lists like the clinic
 * picker on New Case.
 *
 * The popover is rendered via React Portal into document.body with fixed
 * positioning — this is what lets it escape any ancestor `overflow: hidden`
 * (e.g. the .panel wrapper) and avoid clipping.
 *
 * Props:
 *   id           — id for the trigger button (so <label htmlFor=…> works)
 *   name         — passed through onto a hidden input for form semantics
 *   value        — current selected value (single mode), or an array of values
 *                  when `multiple` is set
 *   onChange     — fired with { target: { name, value } } so it slots into
 *                  existing handleChange handlers that read e.target.name/value.
 *                  In `multiple` mode, value is the updated array of values.
 *   multiple     — multi-select: clicking toggles, popover stays open, the
 *                  trigger shows the selected labels (or placeholder when empty)
 *   options      — Array<{ value, label, sub?, swatchClass?, meta?, disabled? }>
 *                  sub = secondary line · swatchClass = colour-dot class ·
 *                  meta = right-aligned text (e.g. a count) · disabled = inert
 *   placeholder  — text shown when nothing is selected
 *   disabled
 *   noOptionsLabel — message when the search yields no matches
 *   popoverZIndex  — stacking order for the portalled popover (must exceed modal overlays)
 *   summary        — multi mode only: (selectedOptions) => trigger text, replacing the
 *                    joined labels (e.g. "3 active" when the picks are listed elsewhere)
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';

const POPOVER_MAX_HEIGHT = 320;
const POPOVER_GAP        = 4;

export default function SearchableSelect({
  id,
  name,
  value,
  onChange,
  options,
  multiple = false,
  placeholder = 'Select…',
  disabled = false,
  noOptionsLabel = 'No matches',
  popoverZIndex = 10001,
  summary = null,
}) {
  const [open, setOpen]           = useState(false);
  const [query, setQuery]         = useState('');
  const [highlight, setHighlight] = useState(0);
  const [popStyle, setPopStyle]   = useState(null);   // { top, left, width, maxHeight, openUp }

  const triggerRef = useRef(null);
  const popRef     = useRef(null);
  const inputRef   = useRef(null);
  const listRef    = useRef(null);

  // Multi-select: a Set of chosen values + the matching option objects (for the
  // trigger summary). Single-select keeps the original `selected` option lookup.
  const selectedSet = useMemo(
    () => new Set(multiple && Array.isArray(value) ? value : []),
    [multiple, value],
  );
  const selectedOptions = useMemo(
    () => (multiple ? options.filter(o => selectedSet.has(o.value)) : []),
    [multiple, options, selectedSet],
  );
  const selected = useMemo(
    () => (multiple ? null : options.find(o => o.value === value) || null),
    [multiple, options, value],
  );

  const isSelected = (o) => (multiple ? selectedSet.has(o.value) : o.value === value);
  const hasSelection = multiple ? selectedOptions.length > 0 : !!selected;
  const triggerLabel = multiple
    ? (selectedOptions.length
        ? (typeof summary === 'function' ? summary(selectedOptions) : selectedOptions.map(o => o.label).join(', '))
        : placeholder)
    : (selected ? selected.label : placeholder);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o =>
      (o.label || '').toLowerCase().includes(q) ||
      (o.sub   || '').toLowerCase().includes(q) ||
      (o.group || '').toLowerCase().includes(q),
    );
  }, [options, query]);

  // Reset highlight when filter or open changes
  useEffect(() => { setHighlight(0); }, [query, open]);

  // Open → focus the search input
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // ── Popover positioning ────────────────────────────────────────────────────
  // Measure the trigger, decide whether to drop down or up, then set fixed coords.
  const updatePosition = useCallback(() => {
    const trig = triggerRef.current;
    if (!trig) return;
    const rect = trig.getBoundingClientRect();
    const vh   = window.innerHeight;
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < POPOVER_MAX_HEIGHT + POPOVER_GAP + 12 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(
      160,
      Math.min(POPOVER_MAX_HEIGHT, openUp ? spaceAbove - POPOVER_GAP - 12 : spaceBelow - POPOVER_GAP - 12)
    );
    setPopStyle({
      top:       openUp ? Math.max(8, rect.top - POPOVER_GAP) : rect.bottom + POPOVER_GAP,
      left:      rect.left,
      width:     rect.width,
      maxHeight,
      openUp,
    });
  }, []);

  // Recalculate on open, scroll (any ancestor), and resize.
  useEffect(() => {
    if (!open) return;
    updatePosition();
    // capture:true so we react to scrolls in any ancestor, not just window
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // ── Outside-click detection ────────────────────────────────────────────────
  // The popover is portalled, so a single rootRef won't contain it. Check both.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (popRef.current?.contains(t))     return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Scroll the highlighted option into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector(`[data-idx="${highlight}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlight]);

  function commit(option) {
    if (!option || option.disabled) return;
    if (multiple) {
      // Toggle membership; keep the popover open + query intact so several can
      // be picked in one go.
      const next = new Set(selectedSet);
      next.has(option.value) ? next.delete(option.value) : next.add(option.value);
      onChange?.({ target: { name, value: [...next] } });
    } else {
      onChange?.({ target: { name, value: option.value } });
      setOpen(false);
      setQuery('');
    }
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(filtered[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  }

  const popover = open && !disabled && popStyle && createPortal(
    <div
      ref={popRef}
      className={`ss-pop dd-shell${popStyle.openUp ? ' dd-up' : ''}`}
      style={{
        position:  'fixed',
        zIndex:    popoverZIndex,
        top:       popStyle.openUp ? undefined : popStyle.top,
        bottom:    popStyle.openUp ? (window.innerHeight - popStyle.top) : undefined,
        left:      popStyle.left,
        width:     popStyle.width,
        maxHeight: popStyle.maxHeight,
      }}
    >
      <input
        ref={inputRef}
        type="text"
        className="input ss-search"
        placeholder="Search…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <ul
        ref={listRef}
        className="ss-list"
        role="listbox"
      >
        {filtered.length === 0 ? (
          <li className="ss-empty">{noOptionsLabel}</li>
        ) : filtered.map((o, i) => {
          const sel = isSelected(o);
          const showGroup = o.group && (i === 0 || filtered[i - 1].group !== o.group);
          return (
            <React.Fragment key={o.value}>
              {showGroup && (
                <li className="ss-group" role="presentation" aria-hidden="true">{o.group}</li>
              )}
              <li
                role="option"
                aria-selected={sel}
                aria-disabled={o.disabled || undefined}
                data-idx={i}
                className={`ss-opt${i === highlight ? ' active' : ''}${sel ? ' selected' : ''}${o.disabled ? ' is-disabled' : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => { e.preventDefault(); commit(o); }}
              >
                <div className="ss-opt-row">
                  {multiple && <span className={`ss-check${sel ? ' on' : ''}`} aria-hidden="true" />}
                  {o.swatchClass && <span className={o.swatchClass} aria-hidden="true" />}
                  <span className="ss-opt-label">{o.label}</span>
                  {o.meta != null && <span className="ss-opt-meta">{o.meta}</span>}
                </div>
                {o.sub && <div className="ss-opt-sub">{o.sub}</div>}
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    </div>,
    document.body,
  );

  return (
    <div className="ss" style={{ position: 'relative' }}>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className="select ss-trigger"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ width: '100%', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span className={`ss-trigger-label${hasSelection ? '' : ' ss-placeholder'}`}>
          {triggerLabel}
        </span>
        {/* A chip that only shows in the list is no use once the list is shut —
            a TEST clinic has to stay obvious while it is the chosen value. */}
        {!multiple && selected?.meta != null && <span className="ss-opt-meta ss-trigger-meta">{selected.meta}</span>}
      </button>
      {/* Hidden input so the value participates in form submissions if needed */}
      <input type="hidden" name={name} value={multiple ? selectedOptions.map(o => o.value).join(',') : (value || '')} readOnly />

      {popover}
    </div>
  );
}
